import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, ClairName } from '../components/layout/DashboardLayout';
import { coachAPI, subscriptionAPI, goalsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Send, Loader2, User, RotateCcw, Sparkles, MessageCircle, Lightbulb, Lock, ChevronRight, Target, Plus, ExternalLink } from 'lucide-react';

// Clair's avatars
const CLAIR_AVATAR = "/clair_avatar.png";  // For chat messages (cropped to C logo)
const CLAIR_WELCOME = "/clair_welcome.png";  // For welcome screen (full image)
import { toast } from 'sonner';

// Nudge messages based on usage percentage
const getNudgeMessage = (used, limit) => {
  const percent = (used / limit) * 100;
  if (percent >= 90) return { message: "Almost at your limit — keep the momentum going!", type: "warning" };
  if (percent >= 75) return { message: "Loving your progress! Upgrade for unlimited access.", type: "soft" };
  if (percent >= 50) return { message: "You're getting great value from Clair!", type: "encourage" };
  return null;
};

export default function CoachPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [usage, setUsage] = useState(null);
  const [pendingGoal, setPendingGoal] = useState(null);
  const [creatingGoal, setCreatingGoal] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await coachAPI.getHistory();
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setInitialLoad(false);
    }
  }, []);

  const loadUsage = useCallback(async () => {
    try {
      const response = await subscriptionAPI.getUsage();
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to load usage data:', error);
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadUsage();
  }, [loadHistory, loadUsage]);

  const handleNewChat = async () => {
    setShowNewChatDialog(false);
    setLoading(true);
    
    try {
      await coachAPI.deleteSession();
      
      // Clear messages and reset state
      setMessages([]);
      setInput('');
      setInitialLoad(false);
      setPendingGoal(null); // Clear any pending goal suggestion
      
      toast.success('New conversation started!');
    } catch (error) {
      toast.error('Failed to start new chat');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Clear any pending goal suggestion when user sends a new message
    setPendingGoal(null);

    const userMessage = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await coachAPI.chat(input);
      const aiMessage = { 
        role: 'assistant', 
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Check if CLaiR suggested a goal
      if (response.data.goal_suggestion) {
        setPendingGoal(response.data.goal_suggestion);
      }
      
      // Refresh usage after sending message
      loadUsage();
    } catch (error) {
      // Check if it's a limit reached error (403)
      if (error.response?.status === 403) {
        setShowUpgradeDialog(true);
      } else {
        toast.error('Failed to get response from AI coach');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle creating a goal from CLaiR's suggestion
  const handleCreateGoal = async () => {
    if (!pendingGoal) return;
    
    setCreatingGoal(true);
    try {
      // Build goal data
      const goalData = {
        title: pendingGoal.title,
        description: pendingGoal.description || '',
        target_date: pendingGoal.target_date || null,
        steps: pendingGoal.steps?.map(text => ({ text, completed: false })) || [],
        notes: pendingGoal.notes || ''
      };
      
      await goalsAPI.create(goalData);
      toast.success(`Goal "${pendingGoal.title}" added to your Goals!`);
      setPendingGoal(null);
    } catch (error) {
      toast.error('Failed to create goal. Try again or add it manually.');
    } finally {
      setCreatingGoal(false);
    }
  };

  // Handle "I'll do it myself" - navigate to goals with prefilled data
  const handleSetupMyself = () => {
    if (!pendingGoal) return;
    // Store goal data in sessionStorage for the Goals page to pick up
    sessionStorage.setItem('prefillGoal', JSON.stringify(pendingGoal));
    setPendingGoal(null);
    navigate('/goals?new=true');
  };

  // Dismiss the goal suggestion
  const handleDismissGoal = () => {
    setPendingGoal(null);
  };

  // Get current nudge based on usage
  const nudge = usage && !usage.is_premium && usage.ai_messages_limit < 999 
    ? getNudgeMessage(usage.ai_messages_used, usage.ai_messages_limit)
    : null;

  const suggestedPrompts = [
    "I'm feeling stuck in my career. Can you help?",
    "How do I set meaningful goals?",
    "I want to improve my work-life balance",
    "Help me identify my core values"
  ];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="coach-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif mb-2">Meet <ClairName /></h1>
            <p className="text-muted-foreground">Your clarity guide inside ClarityPath. Thoughtful questions, grounded insight, no judgment.</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Message Counter for Free Users */}
            {usage && !usage.is_premium && usage.ai_messages_limit < 999 && (
              <div className="text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-full flex items-center gap-2">
                <MessageCircle size={14} />
                <span>{usage.ai_messages_limit - usage.ai_messages_used} of {usage.ai_messages_limit} left</span>
              </div>
            )}
            {messages.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setShowNewChatDialog(true)}
                data-testid="new-chat-btn"
                className="rounded-full"
              >
                <RotateCcw size={16} className="mr-2" />
                New Chat
              </Button>
            )}
          </div>
        </div>

        {/* Progressive Nudge Banner */}
        {nudge && (
          <div className={`p-3 rounded-xl flex items-center justify-between ${
            nudge.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
            nudge.type === 'soft' ? 'bg-primary/5 border border-primary/20' :
            'bg-accent/5 border border-accent/20'
          }`}>
            <p className={`text-sm ${
              nudge.type === 'warning' ? 'text-amber-700' : 'text-muted-foreground'
            }`}>
              {nudge.message}
            </p>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => navigate('/pricing')}
              className="rounded-full text-xs"
            >
              View Plans
            </Button>
          </div>
        )}

        {/* Chat Container */}
        <Card className="rounded-2xl border-border overflow-hidden" data-testid="chat-container">
          {/* Messages */}
          <div className="h-[500px] overflow-y-auto p-6 space-y-6 relative">
            {messages.length === 0 && !initialLoad && (
              <div className="text-center py-12 relative">
                <div className="mx-auto mb-6 w-28 h-28 rounded-full flex items-center justify-center overflow-hidden shadow-lg relative z-10">
                  <img src={CLAIR_AVATAR} alt="Clair" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-serif mb-2 relative z-10">Welcome to <ClairName /></h3>
                <p className="text-muted-foreground mb-8 relative z-10 max-w-md mx-auto">
                  I&apos;m here to guide you toward clarity with thoughtful questions and grounded insight. What&apos;s on your mind?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto relative z-10">
                  {suggestedPrompts.map((prompt, index) => (
                    <button
                      key={`starter-${index}`}
                      onClick={() => setInput(prompt)}
                      data-testid={`suggested-prompt-${index}`}
                      className="p-3 text-sm text-left bg-muted/80 hover:bg-muted rounded-xl duration-300 backdrop-blur-sm border border-border/30"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={message.id || `msg-${index}`}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${index}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden shadow-md">
                    <img src={CLAIR_AVATAR} alt="Clair" className="w-full h-full object-cover" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-4 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-accent-foreground" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-white border border-border flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img src={CLAIR_AVATAR} alt="Clair" className="w-8 h-8 object-contain" />
                </div>
                <div className="bg-muted p-4 rounded-2xl">
                  <Loader2 className="animate-spin text-muted-foreground" size={20} />
                </div>
              </div>
            )}

            {/* Goal Suggestion Card */}
            {pendingGoal && (
              <div className="flex gap-3" data-testid="goal-suggestion-card">
                <div className="w-10 h-10 rounded-full bg-white border border-border flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img src={CLAIR_AVATAR} alt="Clair" className="w-8 h-8 object-contain" />
                </div>
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 p-4 rounded-2xl max-w-md">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="text-primary" size={18} />
                    <span className="font-medium text-sm">Ready to track this goal?</span>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3 mb-3">
                    <p className="font-medium text-foreground">{pendingGoal.title}</p>
                    {pendingGoal.description && (
                      <p className="text-sm text-muted-foreground mt-1">{pendingGoal.description}</p>
                    )}
                    {pendingGoal.target_date && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Target: {new Date(pendingGoal.target_date).toLocaleDateString()}
                      </p>
                    )}
                    {pendingGoal.steps && pendingGoal.steps.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/50">
                        <p className="text-xs text-muted-foreground mb-1">Action steps:</p>
                        <ul className="text-xs text-muted-foreground space-y-0.5">
                          {pendingGoal.steps.slice(0, 3).map((step, i) => (
                            <li key={`goal-step-${i}`}>• {step}</li>
                          ))}
                          {pendingGoal.steps.length > 3 && (
                            <li className="text-muted-foreground/70">+ {pendingGoal.steps.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleCreateGoal}
                      disabled={creatingGoal}
                      className="rounded-full flex-1"
                      data-testid="add-goal-btn"
                    >
                      {creatingGoal ? (
                        <Loader2 className="animate-spin mr-1" size={14} />
                      ) : (
                        <Plus size={14} className="mr-1" />
                      )}
                      Add for me
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSetupMyself}
                      className="rounded-full flex-1"
                      data-testid="setup-myself-btn"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      I'll set it up
                    </Button>
                  </div>
                  <button
                    onClick={handleDismissGoal}
                    className="text-xs text-muted-foreground hover:text-foreground mt-2 w-full text-center"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-border p-4">
            <form onSubmit={handleSend} className="flex gap-3" data-testid="chat-form">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                disabled={loading}
                data-testid="chat-input"
                className="rounded-full"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="send-btn"
                className="rounded-full px-6"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Send size={18} />
                )}
              </Button>
            </form>
          </div>
        </Card>

        {/* Insight Tracker Link */}
        <Card className="rounded-2xl border-border overflow-hidden" data-testid="insight-tracker">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => {
              if (usage?.has_insight_tracker) {
                navigate('/insights');
              }
            }}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                usage?.has_insight_tracker ? 'bg-accent/10' : 'bg-muted'
              }`}>
                {usage?.has_insight_tracker ? (
                  <Lightbulb className="text-accent" size={20} />
                ) : (
                  <Lock className="text-muted-foreground" size={18} />
                )}
              </div>
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  Insight Tracker
                  {!usage?.has_insight_tracker && (
                    <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">Premium</span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {usage?.has_insight_tracker 
                    ? 'See what Clair has learned about you over time'
                    : 'Unlock your relational memory with Supported Path'
                  }
                </p>
              </div>
            </div>
            {usage?.has_insight_tracker ? (
              <ChevronRight className="text-muted-foreground" size={20} />
            ) : (
              <Button 
                size="sm" 
                onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}
                className="rounded-full"
              >
                Upgrade
              </Button>
            )}
          </div>
        </Card>

        <p className="text-xs text-center text-muted-foreground">
          Your AI coach provides guidance based on life coaching principles. For professional mental health support, 
          please consult a licensed therapist.
        </p>
      </div>

      {/* New Chat Confirmation Dialog */}
      <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Start New Conversation?</DialogTitle>
            <DialogDescription>
              This will clear your current chat history with Clair. Your previous conversation will be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowNewChatDialog(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleNewChat}
              className="rounded-full bg-primary"
              data-testid="confirm-new-chat"
            >
              Start New Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog - shown when message limit reached */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="rounded-2xl max-w-md text-center">
          <div className="py-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="text-primary" size={32} />
            </div>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl mb-2">You&apos;re on a roll!</DialogTitle>
              <DialogDescription className="text-base">
                You&apos;ve used all your free coaching messages this month. To continue your conversation with Clair and unlock unlimited guidance, upgrade to a paid plan.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6 space-y-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full rounded-full"
                data-testid="upgrade-btn"
              >
                View Upgrade Options
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowUpgradeDialog(false)}
                className="w-full rounded-full"
              >
                Maybe Later
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Your message limit resets at the start of each month.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
